/* eslint-disable max-len */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';

// Design-system confirmation modal (approve / deny / delete style).
// tone drives the icon-badge color + confirm-button color. Pass an `icon`
// (e.g. a lucide <CheckCircle2 size={28} />) and optional extra body via children
// (e.g. a "reason" textarea). Footer is left-aligned. See DESIGN_SYSTEM.md §8.
const TONE = {
  success: { bg: '#eef5ea', fg: '#53A551', btn: 'bg-[#53A551] hover:bg-[#458a44]' },
  danger: { bg: '#fdebe6', fg: '#CC4525', btn: 'bg-[#CC4525] hover:bg-[#a8391d]' },
  brand: { bg: '#e8eef7', fg: '#3d5a8a', btn: 'bg-[#53A551] hover:bg-[#458a44]' },
};

function ConfirmDialog({
  tone, icon, title, description, confirmLabel, cancelLabel, onConfirm, onCancel, confirming, confirmDisabled, children,
}) {
  const t = TONE[tone] || TONE.brand;
  return (
    // stopPropagation: this modal may be rendered inside a clickable table row
    // (React portals still bubble events up the component tree).
    <Modal open onClose={onCancel} onClick={(e) => e.stopPropagation()} className="flex-row align-c jstfy-c">
      <Box style={{
        width: 512,
        maxWidth: 'calc(100% - 32px)',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
      >
        {icon ? (
          <div className="flex items-center justify-center w-12 h-12 rounded-full" style={{ backgroundColor: t.bg, color: t.fg }}>
            {icon}
          </div>
        ) : null}
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-0">{title}</h5>
        {description ? <div className="text-sm text-ink-500 leading-relaxed">{description}</div> : null}
        {children}
        <div className="flex justify-start items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirming || confirmDisabled}
            className={`inline-flex items-center justify-center px-4 py-2 text-white rounded-[8px] text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition ${t.btn}`}
          >
            {confirming ? `${confirmLabel}…` : confirmLabel}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ConfirmDialog.propTypes = {
  tone: PropTypes.oneOf(['success', 'danger', 'brand']),
  icon: PropTypes.node,
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  confirming: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  children: PropTypes.node,
};

ConfirmDialog.defaultProps = {
  tone: 'brand',
  icon: null,
  description: null,
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  confirming: false,
  confirmDisabled: false,
  children: null,
};

export default ConfirmDialog;
